import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { Link } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2 } from 'lucide-react';

import { useForgotPasswordMutation } from '@/features/auth/store/api';
import { PrimaryButton, GlassCard, Input, FormField } from '@/lib/ui';
import { extractServerError, notify } from '@/lib/utils';
import { ROUTES } from '@/routes';

import { ForgotPasswordSchema } from '../schemas';
import type { ForgotPasswordRequest } from '../types';

export function ForgotPasswordForm() {
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordRequest>({ resolver: zodResolver(ForgotPasswordSchema) });

  const onSubmit = async (data: ForgotPasswordRequest) => {
    try {
      await forgotPassword({ email: data.email }).unwrap();
      setSent(true);
    } catch (err) {
      notify.error(extractServerError(err, 'Failed to send reset link. Please try again.'));
    }
  };

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="flex justify-center">
        <img src="/moveris-logo-dark.svg" alt="Moveris" className="h-10 w-auto" />
      </div>

      <GlassCard padding="lg" className="!bg-[var(--color-glass-bg)] space-y-6">
        {sent ? (
          /* ── Success state ─────────────────────────────────────────── */
          <div className="space-y-4 text-center">
            <div className="flex justify-center">
              <CheckCircle2 className="size-12 text-success" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-white">Check your email</h1>
              <p className="text-sm text-white/60">
                If an account with that address exists, we&apos;ve sent a password reset link. It
                may take a few minutes to arrive.
              </p>
            </div>
            <Link
              to={ROUTES.login}
              className="inline-block text-sm font-medium text-white hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          /* ── Form state ────────────────────────────────────────────── */
          <>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-white">Forgot password?</h1>
              <p className="text-sm text-white/60">
                Enter your email and we&apos;ll send you a reset link.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              <FormField label="Email" htmlFor="forgot-email" error={errors.email?.message}>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="border-white/10 bg-black/40 text-white placeholder:text-white/45 ring-offset-background"
                  {...register('email')}
                />
              </FormField>

              <PrimaryButton type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Sending…' : 'Send reset link'}
              </PrimaryButton>
            </form>

            <p className="text-center text-sm text-white/60">
              Remembered it?{' '}
              <Link to={ROUTES.login} className="font-medium text-white hover:underline">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </GlassCard>
    </div>
  );
}
